import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare, Monitor, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { PageHeader } from '@/components/layout/page-header';
import { Pagination } from '@/components/ui/pagination';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Skeleton } from '@/components/ui/skeleton';
import { useBusiness } from '@/features/auth/auth.store';
import { useStaff } from '@/features/staff/staff.api';
import { formatCurrency } from '@/lib/format';
import { dayBounds, formatStoreTime, storeToday } from '@/lib/store-time';
import { cn } from '@/lib/utils';
import { CheckInSheet } from './check-in-sheet';
import { usePosLanes } from './pos-lanes.api';
import {
  difference,
  useCashReport,
  type CashCheckIn,
} from './cash-drawer.api';

/**
 * What each till took, and whether it balanced.
 *
 * The report is opened for one question — which drawer is short — so that is
 * what the rows lead with. A session that was counted and came out wrong is
 * marked over or under in the row itself; the rest is detail behind it.
 *
 * The original offered "open only", "closed only", "under only" and "over
 * only" as four independent switches, which allowed impossible combinations
 * (open *and* closed). They are one state filter here, which is what the four
 * flags on the request add up to.
 */

type State = 'all' | 'open' | 'closed' | 'under' | 'over';

const PAGE_SIZE = 20;

export function CashDrawerPage() {
  const { t } = useTranslation();
  const business = useBusiness();

  const [range, setRange] = useState(() => ({
    start: storeToday(),
    end: storeToday(),
  }));
  const [laneId, setLaneId] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [state, setState] = useState<State>('all');
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<CashCheckIn | null>(null);

  const lanes = usePosLanes(business?.id ?? null);
  const staff = useStaff(business?.id ?? null);

  const report = useCashReport(
    business
      ? {
          ...dayBounds(range.start, range.end),
          businessId: business.id,
          posLaneId: laneId,
          employeeId,
          page,
          size: PAGE_SIZE,
          // The rows carry their own movements only when asked; the drawer
          // fetches them per check-in, so the list stays light.
          includeMovement: false,
          onlyOpen: state === 'open',
          onlyClosed: state === 'closed',
          onlyUnder: state === 'under',
          onlyOver: state === 'over',
        }
      : null
  );

  const rows = report.data?.checkIns ?? [];
  // `isLoading`, not `isPending`: the report waits on a store being picked.
  const loading = report.isLoading;
  // A filter change keeps the previous answer on screen until the next one
  // arrives, so it is a refresh as much as the button is.
  const busy = report.isFetching && !loading;

  const filters: FilterField[] = [
    {
      id: 'start',
      label: t('cashDrawer.from'),
      type: 'date',
      value: range.start,
      onChange: (start) => {
        setRange((current) => ({ ...current, start }));
        setPage(0);
      },
    },
    {
      id: 'end',
      label: t('cashDrawer.to'),
      type: 'date',
      value: range.end,
      onChange: (end) => {
        setRange((current) => ({ ...current, end }));
        setPage(0);
      },
    },
    {
      id: 'state',
      label: t('cashDrawer.state'),
      value: state,
      sort: false,
      onChange: (value) => {
        setState(value as State);
        setPage(0);
      },
      options: [
        { value: 'all', label: t('cashDrawer.anyState') },
        { value: 'open', label: t('cashDrawer.openOnly') },
        { value: 'closed', label: t('cashDrawer.closedOnly') },
        { value: 'under', label: t('cashDrawer.underOnly') },
        { value: 'over', label: t('cashDrawer.overOnly') },
      ],
    },
    {
      id: 'lane',
      label: t('cashDrawer.lane'),
      value: laneId,
      onChange: (value) => {
        setLaneId(value);
        setPage(0);
      },
      options: [
        { value: '', label: t('cashDrawer.anyLane') },
        ...(lanes.data?.lanes ?? []).map((lane) => ({
          value: lane.id,
          label: lane.tag ?? '',
        })),
      ],
    },
    {
      id: 'employee',
      label: t('cashDrawer.employee'),
      value: employeeId,
      onChange: (value) => {
        setEmployeeId(value);
        setPage(0);
      },
      options: [
        { value: '', label: t('cashDrawer.anyEmployee') },
        ...(staff.data ?? [])
          .filter((person) => person.isActive && person.userId)
          .map((person) => ({
            value: person.userId!,
            label: person.completeName ?? person.email ?? '',
          })),
      ],
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('cashDrawer.title')}
        description={business?.name ?? t('cashDrawer.subtitle')}
        actions={
          <Button
            variant="outline"
            size="sm"
            disabled={report.isFetching}
            onClick={() => void report.refetch()}
          >
            <RefreshCw className={report.isFetching ? 'animate-spin' : ''} />
            <span className="hidden sm:inline">{t('cashDrawer.refresh')}</span>
          </Button>
        }
      />

      <FilterBar busy={report.isFetching} fields={filters} />

      {/* The totals the report already computes across the whole search — the
          original asked for them and then threw them away. */}
      {(loading || report.isSuccess) && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Total
            label={t('cashDrawer.sales')}
            value={report.data?.totalProfit ?? 0}
            loading={loading}
            busy={busy}
          />
          <Total
            label={t('cashDrawer.net')}
            value={report.data?.totalNet ?? 0}
            loading={loading}
            busy={busy}
          />
          <Total
            label={t('cashDrawer.drops')}
            value={report.data?.dropInTotal ?? 0}
            loading={loading}
            busy={busy}
          />
          <Total
            label={t('cashDrawer.balance')}
            value={report.data?.balance ?? 0}
            strong
            loading={loading}
            busy={busy}
          />
        </div>
      )}

      {loading && (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-24 rounded-xl" />
          ))}
        </div>
      )}

      {report.isError && (
        <Empty>{t('cashDrawer.loadFailed')}</Empty>
      )}

      {report.isSuccess && rows.length === 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={busy} />
          <Empty>{t('cashDrawer.noCheckIns')}</Empty>
        </div>
      )}

      {rows.length > 0 && (
        <>
          <div className="relative divide-y rounded-xl border">
            <BusyOverlay show={busy} />
            {rows.map((checkIn) => {
              const gap = difference(checkIn);
              const isOpen = !checkIn.endTime;

              return (
                <button
                  key={checkIn.id}
                  type="button"
                  onClick={() => setSelected(checkIn)}
                  className="hover:bg-accent/60 flex w-full flex-col gap-2 p-3 text-left transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <Monitor className="text-muted-foreground mt-0.5 size-4 shrink-0" />

                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">
                        {checkIn.user?.completeName
                          ?? t('cashDrawer.unknownUser')}
                      </p>
                      <p className="text-muted-foreground truncate text-xs">
                        {checkIn.posLane?.tag ?? t('cashDrawer.unknownLane')} ·{' '}
                        {formatStoreTime(checkIn.startTime)}
                        {checkIn.endTime
                          ? ` → ${formatStoreTime(checkIn.endTime)}`
                          : ''}
                      </p>
                    </div>

                    <div className="flex shrink-0 flex-col items-end gap-1">
                      {isOpen ? (
                        <Badge variant="warning">{t('cashDrawer.open')}</Badge>
                      ) : gap === null || gap === 0 ? (
                        <Badge variant="success">
                          {t('cashDrawer.balanced')}
                        </Badge>
                      ) : (
                        <Badge variant={gap > 0 ? 'secondary' : 'destructive'}>
                          {gap > 0
                            ? t('cashDrawer.over')
                            : t('cashDrawer.under')}{' '}
                          {formatCurrency(Math.abs(gap))}
                        </Badge>
                      )}
                      {checkIn.commentsCount > 0 && (
                        <span className="text-muted-foreground flex items-center gap-1 text-xs">
                          <MessageSquare className="size-3" />
                          {checkIn.commentsCount}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 pl-7 sm:grid-cols-5">
                    <Cell
                      label={t('cashDrawer.opening')}
                      value={checkIn.startAmount}
                    />
                    <Cell
                      label={t('cashDrawer.sales')}
                      value={checkIn.profit}
                    />
                    <Cell
                      label={t('cashDrawer.drops')}
                      value={checkIn.dropInTotal}
                    />
                    <Cell
                      label={t('cashDrawer.expected')}
                      value={checkIn.calculatedEndAmount}
                    />
                    <Cell
                      label={t('cashDrawer.counted')}
                      value={checkIn.endAmount}
                      strong
                    />
                  </div>
                </button>
              );
            })}
          </div>

          <Pagination
            page={page}
            size={PAGE_SIZE}
            total={report.data?.total ?? 0}
            onPageChange={setPage}
          />
        </>
      )}

      <CheckInSheet
        checkIn={selected}
        open={selected !== null}
        onOpenChange={(next) => {
          if (!next) setSelected(null);
        }}
      />
    </div>
  );
}

function Total({
  label,
  value,
  strong,
  loading,
  busy,
}: {
  label: string;
  value: number;
  strong?: boolean;
  /** Nothing to show yet: the figure is a skeleton. */
  loading?: boolean;
  /** A figure is on screen and being refreshed: it stays, under a spinner. */
  busy?: boolean;
}) {
  return (
    <div className="relative rounded-xl border p-3">
      <BusyOverlay show={busy} size="sm" />
      <div className={cn(busy && 'opacity-60 transition-opacity')}>
        <p className="text-muted-foreground text-xs">{label}</p>
        {loading ? (
          <Skeleton className="mt-1 h-7 w-20" />
        ) : (
          <p
            className={cn(
              'tabular-nums',
              strong ? 'text-xl font-semibold' : 'text-lg font-medium'
            )}
          >
            {formatCurrency(value)}
          </p>
        )}
      </div>
    </div>
  );
}

function Cell({
  label,
  value,
  strong,
}: {
  label: string;
  value: number | null;
  strong?: boolean;
}) {
  return (
    <div className="min-w-0">
      <p className="text-muted-foreground truncate text-xs">{label}</p>
      <p
        className={cn(
          'truncate tabular-nums',
          strong ? 'text-sm font-semibold' : 'text-sm'
        )}
      >
        {value == null ? '—' : formatCurrency(value)}
      </p>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-muted-foreground rounded-xl border border-dashed py-12 text-center text-sm">
      {children}
    </p>
  );
}
